/**
 * Workflows list — the SINGLE entry point for both workflow kinds.
 *
 * One screen, grouped into sections by what the workflow *is*:
 *   - Inbound / Internal / Outbound — `kind: canonical`, split by the
 *     immutable `direction` (the canonical contract it's bound to).
 *   - Orchestration — `kind: orchestration`, n8n-style automations with
 *     no canonical binding and no sub-type.
 *
 * Previously these lived on two separate pages (Workflows + Automations);
 * the only thing that ever differed was the `kind` filter, so they're
 * unified here. The editor (same component for every kind) is reached at
 * `/workflows/:id`. The create dialog lets you pick the kind.
 *
 * Templates were removed: to start from an existing workflow use the
 * per-row Duplicate action (or the editor's ⋮ menu).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Boxes, Copy, Info, Plus, Search, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { TagFilter, TagList } from "@/components/tags";
import { WorkflowCreateDialog } from "@/components/workflows/WorkflowCreateDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Tooltip } from "@/components/ui/tooltip";
import { workflowsApi, type WorkflowKind, type WorkflowSummary } from "@/lib/api-workflows";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";

/**
 * The sections (Inbound / Internal / Outbound / Orchestration) ARE the
 * structure of this page, so there is no global pagination: a single pager
 * under multiple grids paginates the FLAT set and then re-groups it, which
 * makes whole sections appear/disappear between pages and the per-section
 * counts lie. A workflow catalogue is bounded (tens, not thousands), so we
 * load the whole list up to this cap and render every section in full. If an
 * instance ever exceeds it, we surface a note rather than silently truncate.
 * 100 is the max the list API accepts for `page_size`.
 */
const LIST_CAP = 100;

/** Kind filter — "all" = both, else narrow to one kind via the API. */
type KindFilter = "all" | WorkflowKind;
const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "canonical", label: "Canonical" },
  { value: "orchestration", label: "Orchestration" },
];

type SectionKey = "inbound" | "internal" | "outbound" | "orchestration";

interface Section {
  key: SectionKey;
  label: string;
  description: string;
  /** Canonical sections show the bound model; orchestration doesn't. */
  showCanonical: boolean;
}

const SECTIONS: Section[] = [
  { key: "inbound", label: "Inbound", description: "External source → canonical", showCanonical: true },
  {
    key: "internal",
    label: "Internal",
    description: "Canonical → canonical (enrich / split / aggregate)",
    showCanonical: true,
  },
  { key: "outbound", label: "Outbound", description: "Canonical → external target", showCanonical: true },
  {
    key: "orchestration",
    label: "Orchestration",
    description: "Free-form automations — free triggers & sinks, no canonical binding",
    showCanonical: false,
  },
];

export function WorkflowsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canWrite = can("workflows", "write");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [tagFilter, setTagFilter] = React.useState<string[]>([]);
  const [kindFilter, setKindFilter] = React.useState<KindFilter>("all");
  const [search, setSearch] = React.useState("");

  // No `kind` filter → both canonical AND orchestration in one list; the
  // segmented control narrows to one kind server-side. We pull the whole
  // list (up to LIST_CAP) and let the sections be the structure — no pager.
  const query = useQuery({
    queryKey: ["workflows", "user", kindFilter, tagFilter],
    queryFn: () =>
      workflowsApi.list({
        kind: kindFilter === "all" ? undefined : kindFilter,
        page: 1,
        page_size: LIST_CAP,
        tag: tagFilter.length > 0 ? tagFilter : undefined,
      }),
  });

  // Duplicate any workflow → land on the editor for the new copy. On a
  // 409 (default "(copy)" name already taken) re-prompt for a name.
  const duplicateMutation = useMutation({
    mutationFn: (src: WorkflowSummary) => workflowsApi.duplicate(src.id),
    onSuccess: (newWf) => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      navigate(`/workflows/${newWf.id}`);
    },
    onError: async (err: Error, src) => {
      const fallback = window.prompt(
        `Couldn't duplicate "${src.name}":\n${err.message}\n\nPick a different name (Cancel to abort):`,
        `${src.name} (copy 2)`,
      );
      if (!fallback) return;
      try {
        const retried = await workflowsApi.duplicate(src.id, { name: fallback });
        qc.invalidateQueries({ queryKey: ["workflows"] });
        navigate(`/workflows/${retried.id}`);
      } catch (e) {
        window.alert(`Still failed: ${(e as Error).message}`);
      }
    },
  });

  const userItems = query.data?.items ?? [];
  const userTotal = query.data?.total ?? 0;

  // Global search across every section, client-side over the loaded set
  // (we already pull the whole catalogue). Matches name, the bound canonical
  // model and the description, so it composes with the Kind + Tag filters
  // rather than replacing them.
  const trimmedSearch = search.trim().toLowerCase();
  const filteredItems = React.useMemo(() => {
    if (!trimmedSearch) return userItems;
    return userItems.filter((w) => {
      const haystack = [w.name, w.canonical_model, w.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(trimmedSearch);
    });
  }, [userItems, trimmedSearch]);

  const grouped = React.useMemo(() => {
    const g: Record<SectionKey, WorkflowSummary[]> = {
      inbound: [],
      internal: [],
      outbound: [],
      orchestration: [],
    };
    filteredItems.forEach((w) => {
      if (w.kind === "orchestration") g.orchestration.push(w);
      else if (w.direction) g[w.direction as Exclude<SectionKey, "orchestration">].push(w);
    });
    return g;
  }, [filteredItems]);

  const renderRow = (w: WorkflowSummary, showCanonical: boolean) => (
    <Tr key={w.id}>
      <Td>
        <div className="flex items-center gap-1.5">
          <Link
            to={`/workflows/${w.id}`}
            className="font-medium text-foreground hover:text-accent"
          >
            {w.name}
          </Link>
          {/* Long descriptions go in a tooltip instead of wrapping under
              every row — keeps the grid to one line per workflow. */}
          {w.description && (
            <Tooltip content={w.description} side="right">
              <span
                tabIndex={0}
                aria-label={`Description: ${w.description}`}
                className="text-muted-foreground/60 transition hover:text-foreground focus:text-foreground focus:outline-none"
              >
                <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
              </span>
            </Tooltip>
          )}
        </div>
      </Td>
      {showCanonical && (
        <Td className="font-mono text-xs">
          {w.canonical_model}.v{w.canonical_version}
        </Td>
      )}
      <Td>
        <TagList tags={w.tags} max={3} />
      </Td>
      <Td className="text-right font-mono text-xs">{w.active_version_number ?? "—"}</Td>
      <Td className="text-right font-mono text-xs">{w.versions_count}</Td>
      <Td>
        {w.enabled ? (
          <Badge tone="active">enabled</Badge>
        ) : (
          <Badge tone="neutral">disabled</Badge>
        )}
      </Td>
      <Td className="text-right">
        <div className="inline-flex items-center gap-1">
          {canWrite && (
            <button
              type="button"
              onClick={() => duplicateMutation.mutate(w)}
              disabled={duplicateMutation.isPending}
              title={`Duplicate "${w.name}" into a new editable copy`}
              aria-label="Duplicate"
              className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <Copy className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}
          <Link
            to={`/workflows/${w.id}`}
            className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-accent"
            title="Open editor"
          >
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
      </Td>
    </Tr>
  );

  const visibleSections = SECTIONS.filter((s) => grouped[s.key].length > 0);

  return (
    <PageContainer>
      <PageHeader
        title={t("workflows.title")}
        description={t("workflows.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            {/* Global search — client-side over every section. Composes with
                the Kind + Tag filters; clears with the inline ✕. */}
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                strokeWidth={1.5}
              />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("workflows.searchHint", "Cerca workflow…")}
                aria-label={t("workflows.searchHint", "Cerca workflow…")}
                className="h-8 w-56 pl-8 pr-7"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              )}
            </div>
            {/* Kind filter — All / Canonical / Orchestration. Narrows the
                list server-side so the sections + pagination stay coherent. */}
            <div className="inline-flex rounded-md border border-border bg-surface p-0.5">
              {KIND_FILTERS.map((opt) => {
                const on = kindFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setKindFilter(opt.value)}
                    className={cn(
                      "rounded px-2.5 py-1 text-xs font-medium transition",
                      on
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <TagFilter
              value={tagFilter}
              onChange={setTagFilter}
              scope="workflows"
            />
            {canWrite && (
              <Button size="sm" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("workflows.newWorkflow")}
              </Button>
            )}
          </div>
        }
      />
      <PageContent>
        {userTotal === 0 ? (
          <EmptyState
            icon={Boxes}
            title="No workflows yet"
            description="Create your first workflow with the New workflow button — pick a Canonical (bound to a model) or Orchestration (free-form) kind. Or duplicate an existing one."
            action={
              canWrite ? (
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  Create from scratch
                </Button>
              ) : null
            }
          />
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={Search}
            title="No matching workflows"
            description={`Nothing matches "${search.trim()}" in the current view. Try a different term, or widen the Kind / Tag filters.`}
            action={
              <Button variant="outline" onClick={() => setSearch("")}>
                Clear search
              </Button>
            }
          />
        ) : (
          <div className="space-y-6">
            {visibleSections.map((section) => {
              const rows = grouped[section.key];
              return (
                <section key={section.key}>
                  <header className="mb-2 flex items-baseline gap-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                      {section.label}
                    </h2>
                    <span className="text-xs text-muted-foreground">
                      {section.description}
                    </span>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                      {rows.length}
                    </span>
                  </header>
                  <div className="rounded-lg overflow-hidden border border-border/60">
                    <Table>
                      <Thead>
                        <Tr>
                          <Th>Name</Th>
                          {section.showCanonical && <Th>Canonical</Th>}
                          <Th>Tags</Th>
                          <Th className="w-24 text-right">Active v.</Th>
                          <Th className="w-24 text-right">Versions</Th>
                          <Th className="w-24">Enabled</Th>
                          <Th className="w-20 text-right" />
                        </Tr>
                      </Thead>
                      <Tbody>{rows.map((w) => renderRow(w, section.showCanonical))}</Tbody>
                    </Table>
                  </div>
                </section>
              );
            })}

            {/* The list is bounded; only surface a note if an instance ever
                blows past the cap, so we never silently hide workflows. */}
            {userTotal > userItems.length && (
              <p className="text-xs text-muted-foreground">
                Showing the first {userItems.length} of {userTotal} workflows.
                Use the Tag or Kind filters to narrow the list.
              </p>
            )}
          </div>
        )}
      </PageContent>

      <WorkflowCreateDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </PageContainer>
  );
}
